import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { invalidateSafety } from '../lib/safety-invalidation';
import { formatInstantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { isJsaEditable, isJsaSubmittable, jsaEditPath, readJsaStep, rejectionReasonText } from '../lib/jsa';
import { toast } from 'sonner';
import {
  X, Download, Loader2, Shield, HardHat, AlertTriangle,
  CheckCircle, XCircle, Clock, Users, MapPin, FileText,
} from 'lucide-react';

const STATUS_COLORS: Record<string, string> = {
  approved: 'bg-green-500/20 text-green-400',
  submitted: 'bg-amber-500/20 text-amber-400',
  draft: 'bg-gray-500/20 text-gray-400',
  rejected: 'bg-red-500/20 text-red-400',
};

/** An instant (created_at, approved_at), shown as the TENANT's day. */
function fmtDate(d: string | null | undefined, timeZone: string | null) {
  return formatInstantDay(d, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';
}

interface JSADetailModalProps {
  jsaId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function JSADetailModal({ jsaId, isOpen, onClose }: JSADetailModalProps) {
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  const { data: jsaRaw, isLoading, isError } = useQuery({
    queryKey: ['safety-jsa-detail', jsaId],
    queryFn: () => api.safety.getJsa(jsaId!).then(r => r.data.data ?? r.data ?? null),
    enabled: isOpen && !!jsaId,
  });

  if (!isOpen || !jsaId) return null;

  const jsa: any = jsaRaw;

  if (isError) {
    toast.error('Failed to load JSA details');
    onClose();
    return null;
  }

  const jobSteps: any[] = jsa?.jobSteps || jsa?.job_steps || [];
  const allSigs: any[] = jsa?.crew_signatures || jsa?.crewSignatures || [];
  const crewSigs = allSigs.filter((s: any) => s.role !== 'supervisor');
  const supervisorSig = allSigs.find((s: any) => s.role === 'supervisor');
  const emergencyProcedures = jsa?.emergencyProcedures || jsa?.emergency_procedures;
  const weatherConsiderations = jsa?.weatherConsiderations || jsa?.weather_considerations;
  const siteHazards: string[] = jsa?.siteSpecificHazards || jsa?.site_specific_hazards || [];
  const status = jsa?.status || 'draft';
  const rejectionReason = jsa?.rejection_reason || jsa?.rejectionReason;
  const approvedAt = jsa?.approved_at || jsa?.approvedAt;
  const jobName = jsa?.jobName || jsa?.job_name || jsa?.job?.name;
  const jobLocation = jsa?.jobLocation || jsa?.job_location || jsa?.job?.location;
  const supervisorName = jsa?.supervisorName || jsa?.supervisor_name ||
    [jsa?.supervisor?.firstName || jsa?.supervisor?.first_name, jsa?.supervisor?.lastName || jsa?.supervisor?.last_name].filter(Boolean).join(' ');

  const handleAction = async (action: 'submit' | 'approve' | 'reject') => {
    if (action === 'reject' && !showRejectInput) {
      setShowRejectInput(true);
      return;
    }
    if (action === 'reject' && !rejectReason.trim()) return;

    setActionLoading(action);
    try {
      if (action === 'submit') await api.safety.submitJsa(jsaId);
      else if (action === 'approve') await api.safety.approveJsa(jsaId);
      else if (action === 'reject') await api.safety.rejectJsa(jsaId, { rejection_reason: rejectReason.trim() });

      const msg = action === 'submit' ? 'JSA submitted for approval'
        : action === 'approve' ? 'JSA approved'
        : 'JSA rejected';
      toast.success(msg);
      invalidateSafety(queryClient);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || `Failed to ${action} JSA`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.safety.getJsaPdf(jsaId);
      if (res?.data) {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `jsa-${jsaId.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const isActioning = !!actionLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto p-4 pt-8 pb-8" onClick={onClose}>
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          </div>
        )}

        {!isLoading && jsa && (
          <>
            {/* Header */}
            <div className="p-6 pb-4 border-b border-fieldiq-border flex-shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-white">{jsa.title || 'Untitled JSA'}</h2>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLORS[status] || 'bg-gray-500/20 text-gray-400'}`}>
                      {status?.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-fieldiq-muted flex items-center gap-1">
                      <Clock size={11} />
                      {fmtDate(jsa.createdAt || jsa.created_at, timeZone)}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {jobName && (
                      <p className="text-xs text-fieldiq-text flex items-center gap-1">
                        <FileText size={11} className="text-fieldiq-muted" />
                        {jobName}{jobLocation ? ` — ${jobLocation}` : ''}
                      </p>
                    )}
                    {!jobName && jobLocation && (
                      <p className="text-xs text-fieldiq-text flex items-center gap-1">
                        <MapPin size={11} className="text-fieldiq-muted" />
                        {jobLocation}
                      </p>
                    )}
                    {supervisorName && (
                      <p className="text-xs text-fieldiq-text flex items-center gap-1">
                        <Shield size={11} className="text-fieldiq-muted" />
                        Supervisor: {supervisorName}
                      </p>
                    )}
                    {crewSigs.length > 0 && (
                      <p className="text-xs text-fieldiq-text flex items-center gap-1">
                        <Users size={11} className="text-fieldiq-muted" />
                        {crewSigs.length} crew signature{crewSigs.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
                <button onClick={onClose} className="text-fieldiq-muted hover:text-white transition-colors flex-shrink-0">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Body — scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* Job Steps */}
              {jobSteps.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-white">Job Steps</h3>
                  {jobSteps.map((step: any, idx: number) => {
                    // Form steps carry `title`, agent steps carry `task` — see readJsaStep.
                    const { label, ppe, hazards, controls } = readJsaStep(step);
                    return (
                      <div key={idx} className="bg-fieldiq-dark rounded-lg p-4 space-y-2.5">
                        <p className="text-sm font-semibold text-white">
                          {idx + 1}. {label}
                        </p>
                        {ppe.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-medium mb-1 flex items-center gap-1">
                              <HardHat size={10} /> PPE
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {ppe.map((item, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-medium">{item}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {hazards.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-medium mb-1 flex items-center gap-1">
                              <AlertTriangle size={10} /> Hazards
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {hazards.map((item, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 font-medium">{item}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {controls.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-medium mb-1 flex items-center gap-1">
                              <Shield size={10} /> Controls
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {controls.map((item, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 font-medium">{item}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {jobSteps.length === 0 && (
                <div className="text-center py-6 text-fieldiq-muted text-sm">No job steps recorded</div>
              )}

              {/* Emergency Procedures */}
              {emergencyProcedures && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">Emergency Procedures</h3>
                  <div className="bg-fieldiq-dark rounded-lg p-4">
                    <p className="text-sm text-fieldiq-text whitespace-pre-wrap">{emergencyProcedures}</p>
                  </div>
                </div>
              )}

              {/* Weather Considerations */}
              {weatherConsiderations && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">Weather Considerations</h3>
                  <div className="bg-fieldiq-dark rounded-lg p-4">
                    <p className="text-sm text-fieldiq-text whitespace-pre-wrap">{weatherConsiderations}</p>
                  </div>
                </div>
              )}

              {/* Site-Specific Hazards */}
              {Array.isArray(siteHazards) && siteHazards.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">Site-Specific Hazards</h3>
                  <div className="bg-fieldiq-dark rounded-lg p-4">
                    <div className="flex flex-wrap gap-1.5">
                      {siteHazards.map((h, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-medium">{h}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Approved timestamp */}
              {status === 'approved' && approvedAt && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                  <p className="text-sm text-green-400 flex items-center gap-2">
                    <CheckCircle size={14} />
                    Approved on {fmtDate(approvedAt, timeZone)}
                  </p>
                </div>
              )}

              {/* Rejection reason */}
              {status === 'rejected' && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  {/* Theme-following ink on the red wash: the wash and border carry
                      the hue. A red-400 ink kept its dark-mode value on the light
                      wash and read under the contrast floor (fe#782). */}
                  <p className="text-xs text-fieldiq-text">
                    <span className="font-semibold">Rejected:</span> {rejectionReasonText(rejectionReason)}
                  </p>
                </div>
              )}

              {/* Inline reject reason input */}
              {showRejectInput && (
                <div>
                  <label className="text-sm text-fieldiq-text block mb-1">Reason for rejection *</label>
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="Enter reason for rejecting this JSA..."
                    rows={3}
                    className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white placeholder-fieldiq-muted focus:border-fieldiq-accent outline-none resize-none"
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* Footer — sticky */}
            <div className="p-4 border-t border-fieldiq-border flex items-center justify-between gap-2 flex-shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  disabled={isActioning}
                  className="px-4 py-2 text-sm text-fieldiq-text hover:text-white rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading || isActioning}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  PDF
                </button>
              </div>
              <div className="flex items-center gap-2">
                {/* Draft or rejected only — the states PUT accepts. Beside Submit,
                    because a rejected JSA resubmitted unchanged sends back the
                    content that was just rejected. */}
                {isJsaEditable(status) && (
                  <button
                    onClick={() => { onClose(); navigate(jsaEditPath(jsaId, pathname)); }}
                    disabled={isActioning}
                    className="px-4 py-2 text-sm font-medium text-fieldiq-text border border-fieldiq-border hover:bg-fieldiq-dark rounded-lg transition-colors disabled:opacity-50"
                  >
                    Edit
                  </button>
                )}
                {isJsaSubmittable(status) && (
                  <button
                    onClick={() => handleAction('submit')}
                    disabled={isActioning}
                    className="px-4 py-2 text-sm font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'submit'
                      ? 'Submitting...'
                      : status === 'rejected' ? 'Resubmit for Approval' : 'Submit for Approval'}
                  </button>
                )}
                {status === 'submitted' && (
                  <>
                    <button
                      onClick={() => handleAction('approve')}
                      disabled={isActioning}
                      className="px-4 py-2 text-sm font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleAction('reject')}
                      disabled={isActioning || (showRejectInput && !rejectReason.trim())}
                      className="px-4 py-2 text-sm font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {actionLoading === 'reject' ? 'Rejecting...' : showRejectInput ? 'Submit Rejection' : 'Reject'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
